import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import Btn from '../../../../UI/Btn'
import Loading from '../../../../UI/Loading'
import MiniHeader from '../../../../UI/MiniHeader'
import Table from '../../../../UI/Table'

import http from '../../../../shared/http'
import { TApi, TRoutes } from '../../../../shared/const'
import { JMC_CLASS_ROLE_LABELS, JMC_GRADE_LABELS, optionsOf } from '../../../../shared/miniCompanies'
import { StatusPill, formatDay } from '../../common/MiniCompany/parts'

import PropTypes from 'prop-types'

import styles from './style.module.scss'

const leer = { name: '', businessDescription: '', grade: '', classRole: '' }

/**
 * Junior Mini Companies der Lehrkraft (Anforderung #92): Liste und Anlegen.
 * Anlegen geht nur an einer Volksschule — das sagt `eligibility`, der Server
 * prüft es beim Speichern noch einmal.
 */
export default function TeacherMiniCompanies({ asCompanies = false }) {
	const { t } = useTranslation()
	const history = useHistory()
	const [items, setItems] = useState(null)
	const [eligibility, setEligibility] = useState(null)
	const [form, setForm] = useState(null)
	const [saving, setSaving] = useState(false)
	const [fehler, setFehler] = useState('')

	useEffect(() => {
		http.get(TApi.TEACHER_MINI_COMPANIES, { disableToast: true })
			.then(({ data }) => setItems(data.items || []))
			.catch(() => {
				setItems([])
				setFehler(t('The Junior Mini Companies could not be loaded.'))
			})
		http.get(TApi.TEACHER_MINI_COMPANIES_ELIGIBILITY)
			.then(({ data }) => setEligibility(data))
			.catch(() => setEligibility({ eligible: false }))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const setFeld = key => event => {
		const value = event.target.value
		setForm(prev => ({ ...prev, [key]: value }))
	}

	const vollstaendig = form && Object.values(form).every(value => String(value).trim())

	const anlegen = event => {
		event.preventDefault()
		if (!vollstaendig) return setFehler(t('Please fill in all fields.'))
		setSaving(true)
		http.post(TApi.TEACHER_MINI_COMPANIES, form)
			.then(({ data }) => history.push(TRoutes.MINI_COMPANY(data.id)))
			.finally(() => setSaving(false))
	}

	const spalten = useMemo(
		() => [
			{ Header: t('Junior Mini Company'), accessor: 'name', width: 240 },
			{
				Header: t('Status'),
				accessor: 'status',
				width: 150,
				Cell: ({ value }) => <StatusPill status={value} />
			},
			{
				Header: t('Year group'),
				accessor: 'grade',
				width: 150,
				Cell: ({ value }) => t(JMC_GRADE_LABELS[value] || value)
			},
			{ Header: t('Children'), accessor: 'participantCount', width: 90, align: 'center' },
			{
				Header: t('School'),
				accessor: 'school',
				width: 220,
				Cell: ({ value }) => (value ? [value.name, value.location].filter(Boolean).join(', ') : '—')
			},
			{ Header: t('Created'), accessor: 'createdAt', width: 110, Cell: ({ value }) => formatDay(value) }
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)

	const hinweis =
		eligibility && !eligibility.eligible
			? eligibility.reason === 'NO_SCHOOL'
				? t('Please assign yourself to a school in your profile first.')
				: t('A Junior Mini Company can only be created at a primary school (Volksschule). Your school is: {{school}}.', {
						school: [eligibility.school?.name, eligibility.school?.type].filter(Boolean).join(' – ')
				  })
			: null

	return (
		<div className={styles.root}>
			{/* Unter „Unternehmen“ (Volksschul-Lehrkraft) trägt die Seite den Namen des Menüpunkts. */}
			<MiniHeader
				className={styles.miniHeader}
				title={asCompanies ? t('companies') : t('Junior Mini Companies')}
				bg="#f8faff"
			>
				{eligibility?.eligible && !form && (
					<Btn onClick={() => setForm(leer)} icon="plus" iconColor="#fff">
						{t('New Junior Mini Company')}
					</Btn>
				)}
			</MiniHeader>

			<p className={styles.intro}>
				{t(
					'The Junior Mini Company is the primary-school version of the Junior Company. You do everything here: create it, enter the children, upload the consent forms and submit it to your state administration for approval.'
				)}
			</p>
			{hinweis && <p className={styles.notice}>{hinweis}</p>}

			{form && (
				<form className={styles.card} onSubmit={anlegen} noValidate>
					<h2 className={styles.cardTitle}>{t('New Junior Mini Company')}</h2>
					<label className={styles.field}>
						{t('Name of the Junior Mini Company')}
						<input type="text" value={form.name} maxLength={120} onChange={setFeld('name')} autoFocus />
					</label>
					<label className={styles.field}>
						{t('Description of the business activity')}
						<textarea value={form.businessDescription} maxLength={5000} onChange={setFeld('businessDescription')} />
						<span className={styles.fieldHint}>{t('What do the children produce or sell? A few sentences are enough.')}</span>
					</label>
					<div className={styles.row}>
						<label className={styles.field}>
							{t('Year group of the pupils')}
							<select value={form.grade} onChange={setFeld('grade')}>
								<option value="">{t('Please select')}</option>
								{optionsOf(JMC_GRADE_LABELS, t).map(o => (
									<option key={o.value} value={o.value}>
										{o.label}
									</option>
								))}
							</select>
						</label>
						<label className={styles.field}>
							{t('Your role in the class')}
							<select value={form.classRole} onChange={setFeld('classRole')}>
								<option value="">{t('Please select')}</option>
								{optionsOf(JMC_CLASS_ROLE_LABELS, t).map(o => (
									<option key={o.value} value={o.value}>
										{o.label}
									</option>
								))}
							</select>
						</label>
					</div>
					{fehler && <p className={styles.error}>{fehler}</p>}
					<div className={styles.actions}>
						<Btn type="button" theme="secondary" onClick={() => {
								setForm(null)
								setFehler('')
							}}>
							{t('Cancel')}
						</Btn>
						<Btn type="submit" loading={saving} disabled={!vollstaendig || saving}>
							{t('Create')}
						</Btn>
					</div>
				</form>
			)}

			{!items && <Loading />}
			{items && !items.length && !form && <p className={styles.empty}>{t('You have not created a Junior Mini Company yet.')}</p>}
			{items && items.length > 0 && (
				<div className="container">
					<Table
						columns={spalten}
						items={items}
						onRowClick={row => history.push(TRoutes.MINI_COMPANY(row.id))}
						rowTitle={t('Open Junior Mini Company')}
					/>
				</div>
			)}
		</div>
	)
}

TeacherMiniCompanies.propTypes = {
	// Als Liste „Unternehmen“ der Volksschul-Lehrkraft (pages/CompaniesList).
	asCompanies: PropTypes.bool
}
